
import { Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import moment from 'moment';
import gdbx from '@/store/modules/gdbx';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		TableFilterable,
	},
})
export default class StockBatchExpiryTable extends FilterWidget {

	public get permissionIds(): PermissionsGroup[] {
		return ['stocks'];
	}

	public get exportFileName() {
		const formattedDate = moment().format('DD MMM YY');
		return 'Stock Batch Expiry' + '_' + formattedDate;
	}

	public get dateFormatted() {
		return moment().format('DD MMM YY');
	}

	public get expensiveHook() {
		const { selectedStockItems, selectedLocations } = this;
		return JSON.stringify([
			selectedStockItems,
			selectedLocations,
		]);
	}

	public get newTableFields(): any[] {
		const table = [
			{
				key: 'status',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'days',
				sortable: true,
				stickyColumn: true,
				formatter: TableItemFormatter.basicInteger,
			},
			{
				key: 'expiry_date',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'location',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'batch_number',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'item_code',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'item_desc',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'qty',
				sortable: true,
				stickyColumn: true,
				formatter: TableItemFormatter.basicInteger,
			},
			{
				key: 'unit_cost',
				sortable: true,
				stickyColumn: true,
				formatter: TableItemFormatter.currencySplitWithDecimals,
			},
		];
		return table;
	}

	@Prop({ default: () => [] }) public selectedLocations!: string[];

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'locations'
	> = ['stockItems', 'locations'];

	public tableItems: any[] = [];
	public tableFields: any[] = [];
	public result: any[] = [];
	public initialTableItems: any[] = [];
	public afterCalculationTableItems: any[] = [];

	public dateFormatter(month: any, year: any) {
		return moment(`${year}-${month}`, 'YYYY-MM').format('x');
	}

	public thousandFormatter(x: any) {
		return x.toLocaleString('en-US');
	}

	public calculateBatchExpiry() {
		const todayDate = moment().valueOf();
		const newArray: any[] = [];

		for (const item of this.initialTableItems) {
			const days = Math.ceil(moment.duration(item.expiryDate - todayDate).asDays());

			const statusFunction = (x: number) => {
				if (x > 0) {
					return 'Going to expire.';
				}
				if (x < 0) {
					return 'Expired.';
				}
				return 'Expires today.';
			};

			newArray.push({
				status: statusFunction(days),
				days,
				expiry_date: moment(item.expiryDate).format('DD/MM/YYYY'),
				location: item.location,
				batch_number: item.batchNo,
				item_code: item.itemCode,
				item_desc: item.itemDesc || '',
				qty: (item.balanceQuantity).toFixed(2),
				unit_cost: item.avgCost,
			});
		}

		this.loading = false;
		this.saveHistory('afterCalculationTableItems');

		return this.afterCalculationTableItems = newArray;
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.4);

		const queryHead = await this._ensureTablesLoaded(
			['stockBatchExpiry', 'globalStocks'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.4, 0.7);

		const batchExpiryTable = getTableName('stockBatchExpiry');
		const globalStocksTable = getTableName('globalStocks');

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const allStockItemsLen = (gdbx.allStockItems && gdbx.allStockItems.length) || 0;
		const stockItemFilter = this.selectedStockItems && this.selectedStockItems.length > 0
			&& this.selectedStockItems.length !== allStockItemsLen
			? ` AND "ITEMCODE" IN (${this.selectedStockItems.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';
		const stockItemFilter2 = this.selectedStockItems && this.selectedStockItems.length > 0
			&& this.selectedStockItems.length !== allStockItemsLen
			? ` AND "CODE" IN (${this.selectedStockItems.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';
		const locationFilter = this.selectedLocations && this.selectedLocations.length > 0
			? ` AND "LOCATION" IN (${this.selectedLocations.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';

		const [batchRows, descRows] = await Promise.all([
			queryHead.rawQuery(`
				SELECT "LOCATION" as location, "ITEMCODE" as itemCode, "BATCH" as batchNo,
					TRY_CAST("EXPDATE" AS BIGINT) as expiryDate,
					TRY_CAST("BALQTY" AS DOUBLE) as balanceQuantity,
					TRY_CAST("AVGUNITCOST" AS DOUBLE) as avgCost
				FROM ${batchExpiryTable}
				WHERE 1=1${stockItemFilter}${locationFilter}
			`),
			queryHead.rawQuery(`
				SELECT "CODE" as itemCode, "DESCRIPTION" as itemDesc
				FROM ${globalStocksTable}
				WHERE 1=1${stockItemFilter2}
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.7, 0.9);

		// Build description map from globalStocks
		const descMap: { [key: string]: string } = {};
		for (const row of descRows) {
			descMap[String(row.itemCode)] = String(row.itemDesc || '');
		}

		// Build initialTableItems with descriptions joined
		this.initialTableItems = batchRows.map((r: any) => ({
			location: String(r.location || ''),
			itemCode: String(r.itemCode || ''),
			batchNo: String(r.batchNo || ''),
			expiryDate: Number(r.expiryDate) || 0,
			balanceQuantity: Number(r.balanceQuantity) || 0,
			avgCost: Number(r.avgCost) || 0,
			itemDesc: descMap[String(r.itemCode)] || '',
		}));

		this.calculateBatchExpiry();
		this.generateLoadingText(1);
	}
}
